import React, { useState, useEffect, useRef } from 'react';
import botNotifySound from '../../../assets/audio/botwrite_notify.mp3';
import { isAppAudioEnabled } from '../../../utils/audioManager';
import { isTTSAudioEnabled } from '../../../configs/config';
import { useUserStore } from '../../../utils/userStore';
import { useAudioStore } from '../../../utils/audioStore';
import { playAppAudio } from '../../../utils/audioManager';
import { useTranslation } from 'react-i18next';
import { msgAvatar, playAudioIcon, pauseAudioIcon, replayAudioIcon } from '../../../assets/images/index';
import MathJaxForChatPage from '../../common/mathjaxforchatpage';
import './bot-message.scss';

import { AudioService } from '../../../services/text-to-audio/texttoaudio.service';

interface BotMessageProps {
  text?: string;
  playAudio?: boolean;
  typingDuration?: number;
  onComplete?: () => void;
  isLoading?: boolean;
  children?: React.ReactNode;
  variant?: 'correct' | 'title' | 'content' | 'summary' | 'error';
  isError?: boolean;
  skipAnimation?: boolean;
  id?: string;
  loadingText?: string;
  onNavigate?: (route: string) => void;
  onReplayAudio?: () => void;
  onTogglePauseAudio?: () => void;
  isAudioPaused?: boolean;
}

const BotMessage: React.FC<BotMessageProps> = ({
  text = '',
  playAudio = true,
  typingDuration = 50,
  onComplete,
  isLoading = false,
  children,
  variant,
  isError = false,
  skipAnimation = false,
  id = 'unknown',
  loadingText,
  onNavigate,
  onReplayAudio,
  onTogglePauseAudio,
  isAudioPaused = false
}) => {
  const { t } = useTranslation();
  const { user_attributes } = useUserStore();
  const playbackState = useAudioStore(state => state.playbackState);
  const [isTyping, setIsTyping] = useState(!skipAnimation);
  const hasTyped = useRef(skipAnimation);

  const targetId = id.replace(/^(title-|content-|quiz-|summary-)/, '');
  const isThisItemActive = playbackState.activeId === targetId || playbackState.activeId === id;
  const isItemPaused = isThisItemActive && playbackState.isPaused;
  const hasAudioControls = isTTSAudioEnabled() && Boolean(onTogglePauseAudio || onReplayAudio);

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (skipAnimation) {
      setIsTyping(false);
      onCompleteRef.current?.();
      return;
    }

    if (isLoading) {
      setIsTyping(true);
      hasTyped.current = false;
      return;
    }

    if (hasTyped.current) {
      setIsTyping(false);
      return;
    }

    const typingTimer = setTimeout(() => {
      setIsTyping(false);
      hasTyped.current = true;
      onCompleteRef.current?.();

      const hasContent = text || children;
      // Skip audio if skipAnimation is true (prevention against race conditions)
      if (playAudio && hasContent && !skipAnimation) {
        playAppAudio(botNotifySound);
      }
    }, typingDuration);

    return () => clearTimeout(typingTimer);
  }, [playAudio, typingDuration, isLoading, text, children, skipAnimation]);

  const bubbleClassName = [
    'v2-bot-message__bubble',
    isTyping ? 'v2-bot-message__bubble--typing' : '',
    variant ? `v2-bot-message__bubble--${variant}` : '',
    isError ? 'v2-bot-message__bubble--error' : '',
    hasAudioControls ? 'v2-bot-message__bubble--has-audio-controls' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="v2-bot-message" id={id}>
      <img src={msgAvatar} alt="bot avatar" className="v2-bot-message__avatar" />
      <div className={bubbleClassName}>
        {isTyping ? (
          <div className="v2-typing-indicator-wrapper">
            {loadingText && (
              <span
                className="v2-loading-text"
                dangerouslySetInnerHTML={{ __html: loadingText }}
              />
            )}
            <div className="v2-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            {children || <MathJaxForChatPage content={text} instant={true} onNavigate={onNavigate} />}
            {hasAudioControls && (
              <div className="v2-bot-message__audio-controls" onClick={(e) => e.stopPropagation()}>
                {onTogglePauseAudio && (
                  <button
                    className="v2-bot-message__audio-btn"
                    onClick={(e) => { e.stopPropagation(); onTogglePauseAudio(); }}
                    aria-label={isItemPaused ? 'Resume Audio' : 'Pause Audio'}
                    title={isItemPaused ? 'Resume Audio' : 'Pause Audio'}
                  >
                    <img 
                      src={isItemPaused ? playAudioIcon : pauseAudioIcon} 
                      alt={isItemPaused ? 'Play' : 'Pause'} 
                    />
                  </button>
                )}
                {onReplayAudio && (
                  <button
                    className="v2-bot-message__audio-btn"
                    onClick={(e) => { e.stopPropagation(); onReplayAudio(); }}
                    aria-label={t('replayAudio')}
                    title={t('replayAudio')}
                  >
                    <img 
                      src={replayAudioIcon} 
                      alt="Replay" 
                    />
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BotMessage;
